import { toast } from "react-toastify";
import { useApiClient } from "@/hooks/useApiClient";
import {
  DownloadPopupBlockedError,
  downloadInNewTab,
} from "@/utils/downloadInNewTab";
import { getCommercialAnalysisDownloadUrlAPI } from "../api";
import type { CommercialRegistrationAnalysisSummary } from "../types";

/**
 * 商業登記サマリのPDFをダウンロードできるか（取得成功・保存先あり・解析IDあり）
 * @param summary 商業登記サマリ
 * @returns ダウンロード可能なら true
 */
export const canDownloadPdf = (
  summary: CommercialRegistrationAnalysisSummary
): boolean =>
  summary.storedPicture?.status === "succeeded" &&
  summary.storedPicture.hasFile &&
  summary.analyzedCorporationId !== null;

/**
 * 取得済み商業登記PDFを押下時にダウンロードする関数を返す。
 * 署名付きURLは画面を開いたままだと失効するため、押下のたびにBEで発行する。失敗時はトーストで知らせる。
 * @param corporateNumber 13桁法人番号
 * @returns サマリを渡すとPDFのダウンロードを開始する関数（失敗しても reject しない）
 */
export const useCommercialPdfDownload = (
  corporateNumber: string
): ((summary: CommercialRegistrationAnalysisSummary) => Promise<void>) => {
  const { apiClient } = useApiClient();
  return async (summary) => {
    if (summary.analyzedCorporationId === null) return;
    const analyzedCorporationId = summary.analyzedCorporationId;
    try {
      // BE が Content-Disposition: attachment の署名URLを発行するため、新しいタブから保存される
      // （S3 の CORS 設定にも依存しない）。ファイル名も BE が指定する。
      await downloadInNewTab(
        async () =>
          await getCommercialAnalysisDownloadUrlAPI(
            apiClient,
            corporateNumber,
            analyzedCorporationId
          )
      );
    } catch (error) {
      toast.error(
        error instanceof DownloadPopupBlockedError
          ? "ポップアップがブロックされました。許可してから再試行してください。"
          : "PDFのダウンロードに失敗しました。再試行してください。"
      );
    }
  };
};
